(() => {
  class CartDrawerRecommendations {
    constructor() {
      this.drawer = document.querySelector('cart-drawer');
      if (!this.drawer) return;

      this.activeIndex = 0;
      this.recoCache = new Map();
      this.autoPlayTimer = null;
      this.viewportScrollHandler = null;
      this.renderTimer = null;
      this.cartUpdateUnsubscriber = null;
      this.prefetched = false;
      this.bindEvents();
      this.observeDrawer();
      this.renderWhenReady();
    }

    bindEvents() {
      document.addEventListener('click', (event) => {
        const addButton = event.target.closest('[data-reco-add]');
        if (addButton) {
          event.preventDefault();
          this.addRecommendedItem(addButton);
          return;
        }

        const dotButton = event.target.closest('[data-reco-dot]');
        if (dotButton) {
          const container = dotButton.closest('[data-cart-reco]');
          const viewport = container ? container.querySelector('[data-reco-viewport]') : null;
          const index = parseInt(dotButton.dataset.recoDot, 10);
          if (!container || !viewport || Number.isNaN(index)) return;
          const card = viewport.querySelectorAll('.cart-reco__card')[index];
          if (!card) return;
          viewport.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
        }
      });

      document.addEventListener('cart:updated', () => {
        this.clearDrawerErrors();
        this.renderWhenReady();
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.clearDrawerErrors();
          this.renderWhenReady();
        });
      }
    }

    async prefetch() {
      try {
        const cart = await this.fetchJSON('/cart.js');
        const anchorProductId = cart && cart.items && cart.items[0] ? cart.items[0].product_id : null;
        if (!anchorProductId) return;
        await this.getRecommendationsCached(anchorProductId);
      } catch (error) {
        console.error('Cart recommendations prefetch failed:', error);
      }
    }

    observeDrawer() {
      const stateObserver = new MutationObserver(() => {
        if (this.drawer.classList.contains('active')) {
          this.prefetchWhenNeeded();
          this.renderWhenReady();
        }
      });

      stateObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });

      const drawerInner = this.drawer.querySelector('.drawer__inner');
      if (!drawerInner) return;

      const contentObserver = new MutationObserver(() => {
        this.renderWhenReady();
      });
      contentObserver.observe(drawerInner, { childList: true });
    }

    renderWhenReady() {
      if (!this.drawer.classList.contains('active')) return;
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => this.renderRecommendations(), 120);
    }

    prefetchWhenNeeded() {
      if (this.prefetched) return;
      this.prefetched = true;
      const run = () => this.prefetch();
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1200 });
      } else {
        setTimeout(run, 300);
      }
    }

    async renderRecommendations() {
      const container = this.drawer.querySelector('[data-cart-reco]');
      const track = container ? container.querySelector('[data-reco-track]') : null;
      const dots = container ? container.querySelector('[data-reco-dots]') : null;
      const viewport = container ? container.querySelector('[data-reco-viewport]') : null;
      if (!container || !track || !dots || !viewport) return;

      try {
        const cart = await this.fetchJSON('/cart.js');
        if (!cart || !cart.items || !cart.items.length) {
          container.style.display = 'none';
          return;
        }

        const anchorProductId = parseInt(container.dataset.productId || `${cart.items[0].product_id || 0}`, 10);
        const inCartIds = new Set(
          (cart.items || []).map((item) => String(item.product_id || '')).filter((id) => id !== '')
        );

        const recProducts = await this.getRecommendationsCached(anchorProductId);
        let products = recProducts.filter((product) => !inCartIds.has(String(product.id)));

        if (!products.length) {
          const allProducts = await this.fetchJSON('/collections/all/products.json?limit=16');
          products = (allProducts.products || [])
            .filter((product) => product.available)
            .filter((product) => !inCartIds.has(String(product.id)));
        }

        products = products.slice(0, 6);
        if (!products.length) {
          container.style.display = 'none';
          return;
        }

        container.style.display = 'block';
        track.innerHTML = products.map((product) => this.productCardHTML(product)).join('');
        dots.innerHTML = products
          .map((_, index) => `<button type="button" class="cart-reco__dot" data-reco-dot="${index}" aria-label="Slide ${index + 1}"></button>`)
          .join('');

        this.updateActiveDot(container, 0);

        this.setupSliderInteractions(container, viewport);
        this.startAutoPlay(container, viewport);
      } catch (error) {
        console.error('Cart recommendations error:', error);
        container.style.display = 'none';
      }
    }

    setupSliderInteractions(container, viewport) {
      if (this.viewportScrollHandler) {
        viewport.removeEventListener('scroll', this.viewportScrollHandler);
      }

      this.viewportScrollHandler = () => {
        const cards = viewport.querySelectorAll('.cart-reco__card');
        if (!cards.length) return;
        const current = Array.from(cards).reduce(
          (closest, card, idx) => {
            const distance = Math.abs(card.offsetLeft - viewport.scrollLeft);
            return distance < closest.distance ? { distance, idx } : closest;
          },
          { distance: Infinity, idx: 0 }
        );
        this.updateActiveDot(container, current.idx);
      };

      viewport.addEventListener('scroll', this.viewportScrollHandler, { passive: true });
      viewport.addEventListener('pointerdown', () => this.stopAutoPlay(), { passive: true });
      viewport.addEventListener('mouseenter', () => this.stopAutoPlay());
      viewport.addEventListener('mouseleave', () => this.startAutoPlay(container, viewport));
      viewport.addEventListener('touchstart', () => this.stopAutoPlay(), { passive: true });
      viewport.addEventListener('touchend', () => this.startAutoPlay(container, viewport), { passive: true });
    }

    startAutoPlay(container, viewport) {
      this.stopAutoPlay();
      const cards = viewport.querySelectorAll('.cart-reco__card');
      if (cards.length <= 1) return;

      this.autoPlayTimer = setInterval(() => {
        const dots = container.querySelectorAll('[data-reco-dot]');
        if (!dots.length) return;
        const nextIndex = (this.activeIndex + 1) % dots.length;
        const nextCard = cards[nextIndex];
        if (!nextCard) return;
        viewport.scrollTo({ left: nextCard.offsetLeft, behavior: 'smooth' });
        this.updateActiveDot(container, nextIndex);
      }, 2800);
    }

    stopAutoPlay() {
      if (this.autoPlayTimer) {
        clearInterval(this.autoPlayTimer);
        this.autoPlayTimer = null;
      }
    }

    clearDrawerErrors() {
      const errorsNode = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (errorsNode) errorsNode.textContent = '';
    }

    updateActiveDot(container, index) {
      const allDots = container.querySelectorAll('[data-reco-dot]');
      allDots.forEach((dot) => dot.classList.toggle('is-active', parseInt(dot.dataset.recoDot, 10) === index));
      this.activeIndex = index;
    }

    async addRecommendedItem(button) {
      const variantId = parseInt(button.dataset.recoVariantId || '0', 10);
      if (!variantId || button.disabled) return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      button.disabled = true;
      button.classList.add('is-loading');

      try {
        const sectionsToRender = drawer.getSectionsToRender().map((section) => section.id);
        const response = await fetch(`${routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionsToRender,
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const parsed = await response.json();
        if (parsed.status) throw new Error(parsed.description || 'Failed to add recommended item');

        if (parsed.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents({
            id: variantId,
            sections: parsed.sections,
          });
        } else {
          await this.refreshDrawerSection();
        }

        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(() => this.renderRecommendations(), 260);
      } catch (error) {
        console.error('Add recommendation failed:', error);
        await this.refreshDrawerSection();
      } finally {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    async refreshDrawerSection() {
      try {
        const htmlText = await fetch(`${routes.cart_url}?section_id=cart-drawer`, {
          headers: { Accept: 'text/html' },
        }).then((response) => response.text());
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        const source = html.querySelector('#CartDrawer');
        const target = document.querySelector('#CartDrawer');
        if (!source || !target) return;
        target.innerHTML = source.innerHTML;
      } catch (error) {
        console.error('Refresh drawer section failed:', error);
      }
    }

    async getRecommendationsCached(productId) {
      if (!productId) return [];
      if (this.recoCache.has(productId)) return this.recoCache.get(productId);

      const url = `/recommendations/products.json?product_id=${productId}&limit=8&intent=related`;
      const result = await this.fetchJSON(url);
      const products = (result.products || []).filter((product) => product.available);
      this.recoCache.set(productId, products);
      return products;
    }

    async fetchJSON(url) {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Request failed: ${url}`);
      return response.json();
    }

    productCardHTML(product) {
      const firstVariant =
        (product.variants || []).find((variant) => variant && variant.available) || (product.variants || [])[0];
      const variantId = firstVariant ? firstVariant.id : 0;
      const imageSrc = product.featured_image || (product.images || [])[0] || '';
      const title = this.escapeHTML(product.title || '');
      const priceCents = this.toCents(firstVariant ? firstVariant.price : null);
      const compareAtCents = this.toCents(firstVariant ? firstVariant.compare_at_price : null);
      const price = this.formatMoney(priceCents);
      const compareAt = compareAtCents > 0 ? this.formatMoney(compareAtCents) : '';
      const discountPercent =
        compareAtCents > priceCents
          ? Math.round(((compareAtCents - priceCents) / compareAtCents) * 100)
          : 0;

      return `
        <article class="cart-reco__card">
          <div class="cart-reco__media">${imageSrc ? `<img src="${imageSrc}" alt="${title}" loading="lazy">` : ''}</div>
          <div class="cart-reco__content">
            <p class="cart-reco__meta">Recommended for you</p>
            <h4 class="cart-reco__title">${title}</h4>
            <p class="cart-reco__price">${price}</p>
            <p class="cart-reco__compare">
              ${compareAt ? `<s>${compareAt}</s>` : ''}
              ${discountPercent ? `<span>${discountPercent}% Off</span>` : ''}
            </p>
          </div>
          <button type="button" class="cart-reco__add" data-reco-add data-reco-variant-id="${variantId}">
            + Add
          </button>
        </article>
      `;
    }

    formatMoney(amount) {
      const cents = this.toCents(amount);
      const normalized = cents / 100;
      return `Rs.${Math.round(normalized).toLocaleString('en-US')}`;
    }

    toCents(amount) {
      if (amount === null || amount === undefined || amount === '') return 0;

      if (typeof amount === 'number') {
        if (!Number.isFinite(amount)) return 0;
        return Number.isInteger(amount) ? amount : Math.round(amount * 100);
      }

      const raw = String(amount).replace(/,/g, '').trim();
      if (!raw) return 0;

      if (raw.includes('.')) {
        const majorUnits = parseFloat(raw);
        return Number.isFinite(majorUnits) ? Math.round(majorUnits * 100) : 0;
      }

      const asInteger = parseInt(raw, 10);
      return Number.isFinite(asInteger) ? asInteger : 0;
    }

    escapeHTML(value) {
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawerRecommendations();
  });
})();
